import React, { useState, useEffect } from 'react';
import { HiOutlineEye, HiOutlineXCircle } from 'react-icons/hi2';
import { FaInbox } from 'react-icons/fa';
import { CajaRecordType } from '../../types';
import RenderCreateCajaRecord from '../CreateCajaRecord/CreateCajaRecord';
import TableDateFormatter from '../../../../../../../../components/common/Table/TableDateFormatter';
import { formatCurrency } from '../../../../../../../../utils/functions';
import CajaRecordDetail from './components/CajaRecordDetail/CajaRecordDetail';
import CancelRecordModal from './components/CancelRecordModal/CancelRecordModal';

interface TableProps {
  data: CajaRecordType[];
  onRecordCreated?: () => void;
  // optional: id of the current local/session so we can conditionally show cancel
  lastSessionId?: number | null;
  // optional setter so child modal can update the list in-place after cancel
  setCurrentData?: (data: CajaRecordType[]) => void;
}

const Table: React.FC<TableProps> = ({ data, onRecordCreated, lastSessionId = null, setCurrentData }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [currentRecord, setCurrentRecord] = useState<CajaRecordType | null>(null);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 900);

  useEffect(() => {
    const handleResize = () => setIsSmallScreen(window.innerWidth < 900);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleOpenDetailModal = (record: CajaRecordType) => {
    setCurrentRecord(record);
    setShowDetailModal(true);
  };

  const handleCancelRecord = (record: CajaRecordType) => {
    setCurrentRecord(record);
    setShowCancelModal(true);
  };

  const renderState = (record: CajaRecordType) => {
    const matched = record.difference === 0;
    if (record.canceled) return <span className="mp-badge mp-badge-danger">Cancelado</span>;
    if (matched) return <span className="mp-badge mp-badge-success">Cuadrado</span>;
    return <span className="mp-badge mp-badge-warning">No Cuadrado</span>;
  };

  const renderDesktopView = () => (
    <div className="mp-table-card">
      <div className="overflow-x-auto">
        <table className="mp-table">
          <thead className="mp-table-head">
            <tr>
              <th>Inicio</th>
              <th>Termino</th>
              <th>Turno</th>
              <th>Estado</th>
              <th>Dif. Restaurant</th>
              <th>Dif. Propina</th>
              <th style={{ textAlign: 'right' }}>Acciones</th>
            </tr>
          </thead>
          <tbody className="mp-table-body">
            {data.map(record => (
              <tr key={record.caja_session_record_id}>
                <td className="opacity-60"><TableDateFormatter data={record.session_start} isRealTime={false} isTimestamp stopCounting /></td>
                <td className="opacity-60"><TableDateFormatter data={record.session_end} isRealTime={false} isTimestamp stopCounting /></td>
                <td className="text-xs opacity-50">{record.shift_name || 'N/A'}</td>
                <td>{renderState(record)}</td>
                <td className="tabular-nums font-medium">{formatCurrency(record.difference)}</td>
                <td className="tabular-nums font-medium">{formatCurrency(record.tip_difference)}</td>
                <td style={{ textAlign: 'right' }}>
                  <div className="flex items-center justify-end gap-1.5">
                    <button className="mp-btn-icon" onClick={() => handleOpenDetailModal(record)} title="Ver detalle">
                      <HiOutlineEye size={16} />
                    </button>
                    {(() => {
                      const recordSessionId = (record as any).session_id ?? (record as any).local_session_id ?? null;
                      const canCancel = !record.canceled && (lastSessionId ? recordSessionId === lastSessionId : true);
                      return canCancel ? (
                        <button className="mp-btn-icon mp-btn-icon-danger" onClick={() => handleCancelRecord(record)} title="Cancelar">
                          <HiOutlineXCircle size={16} />
                        </button>
                      ) : null;
                    })()}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mp-table-footer">
        <span className="text-xs opacity-35">{data.length} registros</span>
      </div>
    </div>
  );

  const renderMobileView = () => (
    <div className="flex flex-col gap-2.5 p-2">
      {data.map(record => {
        const matched = record.difference === 0;
        const accentClass = record.canceled ? 'mp-mobile-card-accent-danger' : matched ? 'mp-mobile-card-accent-success' : 'mp-mobile-card-accent-warning';
        return (
          <div key={record.caja_session_record_id} className={`mp-mobile-card ${accentClass}`} onClick={() => handleOpenDetailModal(record)}>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs opacity-40">
                <TableDateFormatter data={record.session_start} isRealTime={false} isTimestamp stopCounting /> — <TableDateFormatter data={record.session_end} isRealTime={false} isTimestamp stopCounting />
              </span>
              {renderState(record)}
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs opacity-40">{record.shift_name || 'N/A'}</span>
              <div className="flex gap-3">
                <div className="text-right">
                  <div className="text-xs opacity-40">Rest.</div>
                  <div className="text-sm font-semibold tabular-nums" style={{ color: 'var(--text-color)' }}>{formatCurrency(record.difference)}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs opacity-40">Propina</div>
                  <div className="text-sm font-semibold tabular-nums" style={{ color: 'var(--text-color)' }}>{formatCurrency(record.tip_difference)}</div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <>
      {data.length === 0 ? (
        <div className="mp-empty-state">
          <FaInbox className="mp-empty-state-icon" />
          <p className="mp-empty-state-text">No hay registros de arqueo</p>
        </div>
      ) : (
        <>
          {isSmallScreen ? renderMobileView() : renderDesktopView()}
        </>
      )}

      {showCreateModal && <RenderCreateCajaRecord show={showCreateModal} setShow={setShowCreateModal} onRecordCreated={onRecordCreated} />}
      <CancelRecordModal
        show={showCancelModal}
        handleClose={() => setShowCancelModal(false)}
        record={currentRecord}
        currentData={data}
        setCurrentData={setCurrentData ?? (() => {})}
      />
      <CajaRecordDetail show={showDetailModal} setShow={setShowDetailModal} record={currentRecord} />
    </>
  );
};

export default Table;
